import React, { useState, useEffect } from 'react';
import { creditApplications } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, FileText, CheckCircle2, XCircle, Eye } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const map = {
    pending: { className: 'bg-amber-100 text-amber-700', label: 'Pending' },
    approved: { className: 'bg-green-100 text-green-700', label: 'Approved' },
    rejected: { className: 'bg-red-100 text-red-700', label: 'Rejected' },
  };
  const cfg = map[status] || { className: 'bg-gray-100 text-gray-600', label: status };
  return <Badge className={cfg.className} data-testid={`status-${status}`}>{cfg.label}</Badge>;
};

const AddressView = ({ a }) => {
  if (!a) return <span className="text-[#94A3B8]">—</span>;
  const parts = [a.addr_line1, a.addr_line2, a.city, a.state, a.postal].filter(Boolean);
  return <span>{parts.join(', ')}</span>;
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-[#64748B] uppercase tracking-wide">{label}</p>
    <p className="text-sm text-[#0F172A]">{value || <span className="text-[#94A3B8]">—</span>}</p>
  </div>
);

const ReferenceBlock = ({ n, ref }) => {
  if (!ref || (!ref.company_name && !ref.contact_person)) {
    return <p className="text-sm text-[#94A3B8]">Reference {n}: not provided</p>;
  }
  return (
    <div className="border-l-2 border-[#E2E8F0] pl-3">
      <p className="font-medium text-[#0F172A] text-sm">{ref.company_name}</p>
      <p className="text-sm text-[#475569]">{ref.contact_person}</p>
      <p className="text-xs text-[#64748B]">{ref.phone} · {ref.email}</p>
    </div>
  );
};

const AdminCreditApplications = () => {
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [actioning, setActioning] = useState(false);
  const [approvedLimit, setApprovedLimit] = useState('');
  const [paymentTermsDays, setPaymentTermsDays] = useState('30');
  const [rejectionReason, setRejectionReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    load();
  }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await creditApplications.list(params);
      setApps(res.data.applications || []);
    } catch (e) {
      toast.error('Could not load credit applications');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (app) => {
    setSelected(app);
    setSelectedDetail(null);
    setApprovedLimit(String(app.requested_credit_limit || ''));
    setPaymentTermsDays('30');
    setRejectionReason('');
    try {
      const res = await creditApplications.get(app.id);
      setSelectedDetail(res.data);
    } catch (e) {
      toast.error('Could not load application details');
    }
  };

  const approve = async () => {
    const limit = parseFloat(approvedLimit);
    const terms = parseInt(paymentTermsDays, 10);
    if (!limit || limit <= 0) {
      toast.error('Enter a valid credit limit');
      return;
    }
    if (!terms || terms <= 0) {
      toast.error('Select a payment term');
      return;
    }
    setActioning(true);
    try {
      await creditApplications.approve(selected.id, limit, terms);
      toast.success(`Approved with $${limit.toFixed(2)} credit limit and ${terms}-day terms. Customer notified.`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    } finally {
      setActioning(false);
    }
  };

  const reject = async () => {
    setActioning(true);
    try {
      await creditApplications.reject(selected.id, rejectionReason);
      toast.success('Application rejected. Customer notified.');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rejection failed');
    } finally {
      setActioning(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F172A] flex items-center gap-2">
            <FileText className="w-6 h-6" /> Credit Applications
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Review customer credit account requests.</p>
        </div>
        <div className="flex gap-2">
          {['', 'pending', 'approved', 'rejected'].map(s => (
            <Button
              key={s || 'all'}
              size="sm"
              variant={statusFilter === s ? 'default' : 'outline'}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s || 'all'}`}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </Button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-[#002FA7]" /></div>
        ) : apps.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">No credit applications found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="text-left p-3 font-medium text-[#475569]">Submitted</th>
                <th className="text-left p-3 font-medium text-[#475569]">Customer</th>
                <th className="text-left p-3 font-medium text-[#475569]">Business</th>
                <th className="text-left p-3 font-medium text-[#475569]">ABN</th>
                <th className="text-right p-3 font-medium text-[#475569]">Requested</th>
                <th className="text-left p-3 font-medium text-[#475569]">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {apps.map(a => (
                <tr key={a.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`row-${a.id}`}>
                  <td className="p-3 text-[#64748B]">
                    {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString('en-AU') : '—'}
                  </td>
                  <td className="p-3">
                    <p className="font-medium text-[#0F172A]">{a.user_contact || a.user_company || '—'}</p>
                    <p className="text-xs text-[#94A3B8]">{a.user_email}</p>
                  </td>
                  <td className="p-3">{a.business_legal_name}</td>
                  <td className="p-3 font-mono text-xs">{a.abn}</td>
                  <td className="p-3 text-right">${(a.requested_credit_limit || 0).toFixed(0)}</td>
                  <td className="p-3"><StatusBadge status={a.status} /></td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(a)} data-testid={`view-${a.id}`}>
                      <Eye className="w-4 h-4 mr-1" /> Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Credit Application — {selected?.business_legal_name}</DialogTitle>
          </DialogHeader>
          {!selectedDetail ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <StatusBadge status={selectedDetail.status} />
                <span className="text-sm text-[#64748B]">
                  Submitted {new Date(selectedDetail.submitted_at).toLocaleString('en-AU')}
                </span>
              </div>

              <section>
                <h3 className="font-semibold text-[#0F172A] mb-3">A — Business</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Legal Name" value={selectedDetail.business_legal_name} />
                  <Field label="Structure" value={selectedDetail.business_structure} />
                  <Field label="ABN" value={selectedDetail.abn} />
                  <Field label="ACN" value={selectedDetail.acn} />
                  <Field label="Address" value={<AddressView a={selectedDetail.business_address} />} />
                  <Field label="Landline" value={selectedDetail.business_landline} />
                  <Field label="A/P Phone" value={selectedDetail.accounts_payable_phone} />
                  <Field label="Email" value={selectedDetail.business_email} />
                </div>
              </section>

              <section>
                <h3 className="font-semibold text-[#0F172A] mb-3">B — Director 1</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Name" value={`${selectedDetail.director1_first_name} ${selectedDetail.director1_last_name}`} />
                  <Field label="Email" value={selectedDetail.director1_email} />
                  <Field label="Mobile" value={selectedDetail.director1_mobile} />
                  <Field label="Home" value={selectedDetail.director1_is_home} />
                  <Field label="Licence" value={selectedDetail.director1_licence} />
                  <Field label="Agent" value={selectedDetail.director1_agent_details} />
                  <Field label="Residential Address" value={<AddressView a={selectedDetail.director1_residential_address} />} />
                </div>
              </section>

              {(selectedDetail.director2_first_name || selectedDetail.director2_email) && (
                <section>
                  <h3 className="font-semibold text-[#0F172A] mb-3">C — Director 2</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Name" value={`${selectedDetail.director2_first_name} ${selectedDetail.director2_last_name}`} />
                    <Field label="Email" value={selectedDetail.director2_email} />
                    <Field label="Phone" value={selectedDetail.director2_mobile} />
                    <Field label="Home" value={selectedDetail.director2_is_home} />
                    <Field label="Agent" value={selectedDetail.director2_agent_details} />
                    <Field label="Residential Address" value={<AddressView a={selectedDetail.director2_residential_address} />} />
                  </div>
                </section>
              )}

              <section>
                <h3 className="font-semibold text-[#0F172A] mb-3">D — Bank Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Bank" value={selectedDetail.bank_name} />
                  <Field label="Account Name" value={selectedDetail.bank_account_name} />
                  <Field label="BSB" value={selectedDetail.bank_bsb} />
                  <Field label="Account Number" value={selectedDetail.bank_account_number} />
                </div>
              </section>

              <section>
                <h3 className="font-semibold text-[#0F172A] mb-3">E — Operations</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="# Employees" value={selectedDetail.number_of_employees} />
                  <Field label="Premises Status" value={selectedDetail.business_premises_status} />
                  <Field label="Agent" value={selectedDetail.business_premises_agent} />
                  <Field label="Details" value={selectedDetail.business_premises_details} />
                </div>
              </section>

              <section>
                <h3 className="font-semibold text-[#0F172A] mb-3">F — Background</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Director bankrupt?" value={selectedDetail.director_bankrupt ? 'Yes' : 'No'} />
                  <Field label="Details" value={selectedDetail.director_bankrupt_details} />
                </div>
              </section>

              <section>
                <h3 className="font-semibold text-[#0F172A] mb-3">G — Trade References</h3>
                <div className="space-y-3">
                  <ReferenceBlock n={1} ref={selectedDetail.trade_ref1} />
                  <ReferenceBlock n={2} ref={selectedDetail.trade_ref2} />
                  <ReferenceBlock n={3} ref={selectedDetail.trade_ref3} />
                </div>
              </section>

              <section>
                <h3 className="font-semibold text-[#0F172A] mb-3">H — Declaration</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Requested Limit" value={`$${(selectedDetail.requested_credit_limit || 0).toFixed(2)}`} />
                  <Field label="Signer" value={`${selectedDetail.signer_first_name} ${selectedDetail.signer_last_name}`} />
                  <Field label="Position" value={selectedDetail.signer_position} />
                  <Field label="Date Signed" value={selectedDetail.signed_date} />
                </div>
                {selectedDetail.signature_data && (
                  <div className="mt-3">
                    <p className="text-xs text-[#64748B] uppercase tracking-wide mb-1">Signature</p>
                    <img src={selectedDetail.signature_data} alt="signature" className="border border-[#E2E8F0] bg-white rounded max-w-md max-h-32" />
                  </div>
                )}
              </section>

              {selectedDetail.status === 'pending' && (
                <section className="border-t border-[#E2E8F0] pt-6 space-y-4">
                  <h3 className="font-semibold text-[#0F172A]">Decision</h3>
                  <p className="text-xs text-[#64748B]">
                    Customer requested: <span className="font-medium">${(selectedDetail.requested_credit_limit || 0).toFixed(2)}</span>. You can approve for a different amount.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#64748B] mb-1">Approved credit limit (AUD)</p>
                      <Input
                        type="number"
                        min="0"
                        step="100"
                        value={approvedLimit}
                        onChange={e => setApprovedLimit(e.target.value)}
                        placeholder="e.g. 5000"
                        data-testid="approve-limit-input"
                      />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B] mb-1">Payment terms</p>
                      <select
                        value={paymentTermsDays}
                        onChange={e => setPaymentTermsDays(e.target.value)}
                        className="w-full h-10 px-3 border border-[#E2E8F0] rounded-md bg-white text-sm"
                        data-testid="payment-terms-select"
                      >
                        <option value="7">7 days</option>
                        <option value="14">14 days</option>
                        <option value="21">21 days</option>
                        <option value="30">30 days</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-[#64748B] mb-1">Rejection reason (optional, sent to customer)</p>
                    <Textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} data-testid="rejection-reason" />
                  </div>
                </section>
              )}

              {selectedDetail.status === 'approved' && (
                <section className="border-t border-[#E2E8F0] pt-6 grid grid-cols-2 gap-4">
                  <Field label="Approved Limit" value={`$${(selectedDetail.approved_limit || 0).toFixed(2)}`} />
                  <Field label="Payment Terms" value={selectedDetail.approved_payment_terms_days ? `${selectedDetail.approved_payment_terms_days} days` : '30 days'} />
                </section>
              )}

              {selectedDetail.status === 'rejected' && (
                <section className="border-t border-[#E2E8F0] pt-6">
                  <Field label="Rejection Reason" value={selectedDetail.rejection_reason} />
                </section>
              )}
            </div>
          )}
          {selectedDetail?.status === 'pending' && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelected(null)} disabled={actioning}>Cancel</Button>
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={reject} disabled={actioning} data-testid="reject-btn">
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button onClick={approve} disabled={actioning} data-testid="approve-btn">
                {actioning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Approve
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCreditApplications;
